import React, { useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useUserRole } from "../../context/UserRoleContext";
import { useNotifications } from "../../context/NotificationContext";
import { useMessaging } from "../../context/MessagingContext";
import { logOut } from "../../config/firebase";
import llmLogo from "../../assets/images/llmIcon.png";
import BarSelector from "./BarSelector";
import ArtistSelector from "./ArtistSelector";

const Sidebar = ({
  currentPage,
  setPage,
  isMobileMenuOpen,
  setIsMobileMenuOpen,
}) => {
  const { currentUser } = useAuth();
  const { userData } = useUserRole();
  const { unreadCount: notificationUnreadCount } = useNotifications();
  const { unreadCount: messageUnreadCount } = useMessaging();
  const navigate = useNavigate();
  const location = useLocation();
  const [logoutPending, setLogoutPending] = useState(false);
  const [logoutError, setLogoutError] = useState(null);

  const handleLogout = async () => {
    if (logoutPending) return;
    setLogoutError(null);
    setLogoutPending(true);
    try {
      await logOut();
      window.location.href = "/";
    } catch (error) {
      console.error("Logout error:", error);
      setLogoutError(
        error?.message || "Could not sign out. Please try again.",
      );
      setLogoutPending(false);
    }
  };

  const handleMenuClick = (pageId) => {
    // Navigate based on user role
    const basePath =
      userData?.role === "artist"
        ? "/artist"
        : userData?.role === "bar_owner"
        ? "/bar-owner"
        : "/bar-admin";

    if (pageId === "dashboard") {
      navigate(basePath);
    } else {
      navigate(`${basePath}/${pageId}`);
    }

    // Close mobile menu after selection
    if (isMobileMenuOpen) {
      setIsMobileMenuOpen(false);
    }
  };

  // Determine current page from URL
  const getCurrentPageId = () => {
    const path = location.pathname;
    const segments = path.split("/").filter(Boolean);

    if (segments.length === 1) {
      return "dashboard";
    }

    return segments[segments.length - 1] || "dashboard";
  };

  const activePageId = getCurrentPageId();

  // Get menu items based on user role
  const getMenuItems = () => {
    if (!userData) return [];

    switch (userData.role) {
      case "artist":
        return [
          {
            id: "dashboard",
            label: "Dashboard",
            icon: "🏠",
            category: "Overview",
          },
          {
            id: "calendar",
            label: "Calendar",
            icon: "📅",
            category: "Scheduling",
          },
          {
            id: "groups",
            label: "Groups",
            icon: "👥",
            category: "Overview",
          },
          {
            id: "bulletinBoard",
            label: "Bulletin Board",
            icon: "📌",
            category: "Overview",
          },
          {
            id: "availability",
            label: "Availability",
            icon: "⏰",
            category: "Scheduling",
          },
          {
            id: "earnings",
            label: "Earnings",
            icon: "💰",
            category: "Metrics",
          },
          {
            id: "messages",
            label: "Messages",
            icon: "💬",
            category: "Communication",
          },
          {
            id: "notifications",
            label: "Notifications",
            icon: "🔔",
            category: "Communication",
          },
          {
            id: "settings",
            label: "Settings",
            icon: "🔧",
            category: "Profile",
          },
        ];

      case "bar_owner":
        return [
          {
            id: "dashboard",
            label: "Dashboard",
            icon: "🏠",
            category: "Overview",
          },
          {
            id: "calendar",
            label: "Calendar",
            icon: "📅",
            category: "Music",
          },
          {
            id: "artists",
            label: "Artists",
            icon: "🎵",
            category: "Music",
          },
          {
            id: "financials",
            label: "Financials",
            icon: "💰",
            category: "Music",
          },
          {
            id: "messages",
            label: "Messages",
            icon: "💬",
            category: "Communication",
          },
          {
            id: "notifications",
            label: "Notifications",
            icon: "🔔",
            category: "Communication",
          },
          {
            id: "management",
            label: "Management",
            icon: "⚙️",
            category: "House Keeping",
          },
          {
            id: "settings",
            label: "Settings",
            icon: "🔧",
            category: "House Keeping",
          },
        ];

      case "bar_admin":
        return [
          {
            id: "dashboard",
            label: "Dashboard",
            icon: "🏠",
            category: "Overview",
          },
          {
            id: "calendar",
            label: "Calendar",
            icon: "📅",
            category: "Music",
          },
          {
            id: "artists",
            label: "Artists",
            icon: "🎵",
            category: "Music",
          },
          {
            id: "financials",
            label: "Financials",
            icon: "💰",
            category: "Music",
          },
          {
            id: "messages",
            label: "Messages",
            icon: "💬",
            category: "Communication",
          },
          {
            id: "notifications",
            label: "Notifications",
            icon: "🔔",
            category: "Communication",
          },
          {
            id: "management",
            label: "Management",
            icon: "⚙️",
            category: "House Keeping",
          },
          {
            id: "settings",
            label: "Settings",
            icon: "🔧",
            category: "House Keeping",
          },
        ];

      default:
        return [];
    }
  };

  const menuItems = getMenuItems();

  return (
    <>
      {logoutPending &&
        createPortal(
          <div
            className="logout-in-progress-overlay"
            role="alertdialog"
            aria-busy="true"
            aria-live="polite"
            aria-label="Signing out"
          >
            <div className="logout-in-progress-content">
              <div className="spinner" aria-hidden="true" />
              <p className="logout-in-progress-message">Signing out…</p>
            </div>
          </div>,
          document.body,
        )}
      {/* Mobile overlay */}
      {isMobileMenuOpen && (
        <div
          className="mobile-overlay"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`sidebar ${isMobileMenuOpen ? "sidebar-open" : ""}`}>
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <img
              src={llmLogo}
              alt="Local Live Music Logo"
              className="sidebar-logo-img"
            />
            <h1 className="sidebar-title">Local Live Booking</h1>
          </div>
          <p className="sidebar-subtitle">
            {userData?.role === "artist" && "Artist Portal"}
            {userData?.role === "bar_owner" && "Venue Management"}
            {userData?.role === "bar_admin" && "Admin Portal"}
          </p>
          {userData?.role === "bar_owner" && (
            <div className="sidebar-bar-selector">
              <BarSelector />
            </div>
          )}
          {userData?.role === "bar_admin" && (
            <div className="sidebar-bar-selector">
              <BarSelector showWhenNoBars emptyBarLabel="No bars assigned" />
            </div>
          )}
          {userData?.role === "artist" && (
            <div className="sidebar-bar-selector">
              <ArtistSelector />
            </div>
          )}
        </div>

        <nav className="sidebar-nav">
          {menuItems.map((item) => {
            const hasUnreadNotifications = item.id === "notifications" && notificationUnreadCount > 0;
            const hasUnreadMessages = item.id === "messages" && messageUnreadCount > 0;
            const unreadCount = hasUnreadNotifications 
              ? notificationUnreadCount 
              : hasUnreadMessages 
              ? messageUnreadCount 
              : 0;

            return (
              <button
                key={item.id}
                onClick={() => handleMenuClick(item.id)}
                className={`nav-item ${
                  activePageId === item.id ? "nav-item-active" : ""
                }`}
              >
                <span className="nav-icon-wrapper">
                  <span className="nav-icon">{item.icon}</span>
                  {unreadCount > 0 && (
                    <span className="nav-item-badge">
                      {unreadCount > 99 ? "99+" : unreadCount}
                    </span>
                  )}
                </span>
                <span className="nav-label">{item.label}</span>
              </button>
            );
          })}
        </nav>

        <div className="sidebar-footer">
          <div className="user-info">
            <p className="user-name">{userData?.name || currentUser?.email}</p>
            <p className="user-role">
              {userData?.role?.replace("_", " ").toUpperCase()}
            </p>
          </div>
          {logoutError && (
            <p className="logout-error" role="alert">
              {logoutError}
            </p>
          )}
          <button
            type="button"
            onClick={handleLogout}
            className="logout-btn"
            disabled={logoutPending}
            aria-busy={logoutPending}
          >
            {logoutPending ? (
              <span className="logout-label">Signing out…</span>
            ) : (
              <>
                <span className="logout-icon">🚪</span>
                <span className="logout-label">Logout</span>
              </>
            )}
          </button>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
